import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { USAGE_PING_MS, usageAreaFromPath } from '@pointage/shared';
import { apiClient } from '../api/client.js';

// Connexions & usage — the client half of the tracker.
//
// API calls alone can't answer "how long was the app actually used":
// React Query caches for a minute, so someone reading the grille for
// twenty minutes makes no requests at all. This heartbeat is the
// missing signal — one tiny POST a minute, only while the tab is
// visible, carrying the area being looked at.
//
// Hidden tabs stay silent on purpose: a window left open behind
// another one is not usage, and the server's idle-gap rule turns that
// silence into excluded time rather than inflated time.

export function useUsageHeartbeat(enabled = true): void {
  const { pathname } = useLocation();
  const areaRef = useRef(usageAreaFromPath(pathname));
  areaRef.current = usageAreaFromPath(pathname);

  useEffect(() => {
    if (!enabled) return;

    let stopped = false;
    const ping = () => {
      if (stopped || document.visibilityState !== 'visible') return;
      // Fire and forget: a failed heartbeat is not worth a retry, and
      // must never surface to the user. Passive tracking on the next
      // real API call covers the gap anyway.
      void apiClient.post('/usage/ping', { area: areaRef.current }).catch(() => {});
    };

    ping();
    const timer = window.setInterval(ping, USAGE_PING_MS);
    // Coming back to the tab is itself a sign of life — ping at once so
    // the session doesn't look idle for up to a minute.
    document.addEventListener('visibilitychange', ping);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', ping);
    };
  }, [enabled]);

  // A navigation is a sign of life too, and it's what re-attributes
  // the time to the screen actually being used.
  useEffect(() => {
    if (!enabled || document.visibilityState !== 'visible') return;
    void apiClient.post('/usage/ping', { area: usageAreaFromPath(pathname) }).catch(() => {});
  }, [pathname, enabled]);
}
